import React, { useState } from 'react';
import InfoBadge from '../../Elements/InfoBadge';
import WarningBadge from '../../Elements/WarningBadge';
import { IOB_GENERATION_DAY_OF_MONTH } from './fteTrackingUtils';

/**
 * Next automated IOB (salary + bonus) generation: the first evaluation on/after the 7th of the month.
 * Unlike the FTE row copy below, the exact hour is not fixed — the importer is evaluated roughly daily,
 * whenever its frequency anchor comes due — so only the DATE is shown for it. Treated as pending for the
 * whole of the 7th (UTC): on the 7th itself the run may or may not have happened yet, and showing today
 * is the less misleading of the two.
 */
function getNextIobRunDate() {
  const now = new Date();
  let year = now.getUTCFullYear();
  let month = now.getUTCMonth(); // 0-based
  if (now.getUTCDate() > IOB_GENERATION_DAY_OF_MONTH) {
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }
  return new Date(Date.UTC(year, month, IOB_GENERATION_DAY_OF_MONTH));
}

// The automated FTE row copy (CaspianDatabaseService) still runs on the 1st at 01:00 UTC — only the
// invoice runs moved to the 7th.
function getNextFteCopyRun() {
  const now = new Date();
  let year = now.getUTCFullYear();
  let month = now.getUTCMonth(); // 0-based
  const thisMonthRun = new Date(Date.UTC(year, month, 1, 1, 0));
  if (now >= thisMonthRun) {
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }
  return new Date(Date.UTC(year, month, 1, 1, 0));
}

function formatLocalTime(utcDate) {
  return utcDate.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function formatUtcDate(utcDate) {
  return `${String(utcDate.getUTCDate()).padStart(2, '0')}/${String(utcDate.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}/${utcDate.getUTCFullYear()}`;
}

function getIobTargetMonthLabel(runDate) {
  // IOB processes the previous month relative to the run date
  const d = new Date(runDate);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function getFteAutoGenTargetMonthLabel(runDate) {
  // Auto FTE generation creates records for the run month (based on previous month's data)
  return `${runDate.getUTCFullYear()}/${String(runDate.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function FteIobWarnings({ warnings }) {
  // Which item's mobile popover is open (index into items), or null. On phones the badges shrink to
  // icon chips sitting inline with the month switcher; tapping a chip reveals the badge's text as a
  // popover under it. Desktop shows the full badges and never renders the chips (CSS-swapped).
  const [openIndex, setOpenIndex] = useState(null);

  if (!warnings) return null;

  const monthLabel = `${warnings.year}/${String(warnings.month).padStart(2, '0')}`;
  const nextIobRun = getNextIobRunDate();
  const nextFteCopyRun = getNextFteCopyRun();
  const iobMonth = getIobTargetMonthLabel(nextIobRun);
  const fteMonth = getFteAutoGenTargetMonthLabel(nextFteCopyRun);

  const items = [];
  if (warnings.notLockedCount > 0) {
    items.push({
      kind: 'warn',
      icon: 'fa-solid fa-triangle-exclamation',
      text: (
        <>
          <strong>{warnings.notLockedCount}</strong> not locked for <strong>{monthLabel}</strong>, lock before IOB
          generation
        </>
      ),
      secondaryText: null,
    });
  }
  items.push({
    kind: 'info',
    icon: 'fa-solid fa-circle-info',
    text: (
      <>
        Next automated IOB generation for <strong>{iobMonth}</strong>
      </>
    ),
    secondaryText: formatUtcDate(nextIobRun),
  });
  items.push({
    kind: 'info',
    icon: 'fa-solid fa-clone',
    text: (
      <>
        Next automated FTE generation for <strong>{fteMonth}</strong>
      </>
    ),
    secondaryText: formatLocalTime(nextFteCopyRun),
  });

  return (
    <div className='fte-iob-warnings d-flex align-items-center flex-wrap gap-3'>
      {items.map((item, index) => (
        <span
          className='fte-info-item'
          key={index}
        >
          <span className='fte-info-full'>
            {item.kind === 'warn' ? (
              <WarningBadge
                icon={item.icon}
                text={item.text}
              />
            ) : (
              <InfoBadge
                icon={item.icon}
                text={item.text}
                secondaryText={item.secondaryText}
              />
            )}
          </span>
          <button
            type='button'
            className={`fte-info-chip${item.kind === 'warn' ? ' bg-warning text-dark border-warning' : ''}`}
            title='Details'
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          >
            <i className={item.icon} />
          </button>
          {openIndex === index && (
            <span className='fte-info-popover'>
              {item.text}
              {item.secondaryText && <span className='text-muted ms-1'>({item.secondaryText})</span>}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}
